import React from 'react';
import { Check as CheckIcon } from 'lucide-react';
import { SaveOption } from '../SaveDialog.types';
import Dropdown, { DropdownOption } from './Dropdown';
import { FormError } from './FormError';
import { InlineError } from './InlineError';
import { Spinner } from './Spinner';
import {
  StatLinkClassProp,
  DataDtm,
  updateGMDigitalDataSiteSection,
} from '../../../utils/externalAnalyticsGM';
import { uimetadata } from '../../../utils/metadata';
import { useRTL } from '../../../utils/translations';
import { useLogEvent, AnalyticsIds } from '../../../utils/useAnalytics';

interface FormViewProps {
  selectedOption: SaveOption | null;
  selectedPrefix: string;
  setSelectedPrefix: (prefix: string) => void;
  isMarketingAccepted: boolean;
  setIsMarketingAccepted: (accepted: boolean) => void;
  backendError: string | null;
  setBackendError: (error: string | null) => void;
  setFieldValue: (field: string, value: string) => void;
  loading: boolean;
  /**
   * Save-to-WhatsApp / Save-to-Email stay on the form after submitting and just
   * flip the footer CTA to "Sent!", the way 1.0's SaveNShare does. The booking
   * forms hand off to SuccessView instead and never see this.
   */
  isSent?: boolean;
  isFormValid: boolean;
  dealers: any[];
  models: string[];
  errors: any;
  handleSendConfig: () => void;
  validateSingleField: (field: string) => void;
  clearError: (field: string) => void;
  t: (key: string, fallback?: string) => string;
  // Individual fields for backward compat or direct use if needed
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedModel: string;
  selectedDealer: string;
  testDriveDate: string;
  testDriveTime: string;
  comments: string;
}

const FormView: React.FC<FormViewProps> = ({
  selectedOption,
  selectedPrefix,
  setSelectedPrefix,
  isMarketingAccepted,
  setIsMarketingAccepted,
  backendError,
  setBackendError,
  setFieldValue,
  loading,
  isSent = false,
  isFormValid,
  dealers,
  models,
  errors,
  handleSendConfig,
  validateSingleField,
  clearError,
  t,
  firstName,
  lastName,
  email,
  phone,
  selectedModel,
  selectedDealer,
  testDriveDate,
  testDriveTime,
  comments,
}) => {
  const { isRTL } = useRTL();
  const { trackEvent } = useLogEvent();
  const isBooking =
    selectedOption === 'test-drive' || selectedOption === 'callback';

  const LEAD_NAME_BY_OPTION: Record<string, string> = {
    'test-drive': 'testDrive',
    callback: 'callBack',
    whatsapp: 'saveToWhatsapp',
    email: 'saveToEmail',
  };
  const leadFormName = selectedOption ? LEAD_NAME_BY_OPTION[selectedOption] : '';

  const hasStartedRef = React.useRef(false);
  const trackFieldStart = (fieldId: string) => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    trackEvent(AnalyticsIds.leadFormStart, {
      parent: leadFormName,
      primaryTypeName: fieldId,
    });
  };

  // Dial codes come from the project's own metadata (config.countryCodes, the
  // same source 1.0 reads) so a project that only serves, say, KSA does not
  // offer prefixes it never accepts. Falls back to the previous hardcoded list.
  const prefixes = React.useMemo(() => {
    const codes = uimetadata.config?.countryCodes;
    const fromMetadata = Object.values(codes || {})
      .map((entry) => (typeof entry === 'string' ? entry : entry?.code))
      .filter((code): code is string => !!code);
    const unique = Array.from(new Set(fromMetadata));
    return unique.length
      ? unique
      : ['+971', '+966', '+965', '+973', '+974', '+968', '+91', '+1'];
  }, []);

  // No slot in the past; the showroom cannot honour one.
  const today = React.useMemo(() => new Date().toISOString().split('T')[0], []);

  const formData: Record<string, string> = {
    firstName,
    lastName,
    email,
    phone,
    model: selectedModel,
    dealer: selectedDealer,
    testDriveDate,
    testDriveTime,
    comments,
  };

  const renderField = (field: any) => {
    const { id, type, labelKey, placeholderKey } = field;
    const label = t(`staticTranslations.${labelKey}`);
    const placeholder = placeholderKey
      ? t(`staticTranslations.${placeholderKey}`)
      : label;
    const value = formData[id] || '';
    const hasError = !!errors[id];

    if (type === 'text' || type === 'email' || type === 'phone') {
      const isPhone = type === 'phone';
      return (
        <div className="save-dialog__input-container" key={id}>
          <div className="flex gap-[16px]">
            {isPhone && (
              <div className="save-dialog__input-container--prefix">
                <Dropdown
                  disabled={loading}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  trigger={({ open }: { open: boolean }) => (
                    <div
                      className={`save-dialog__phone-prefix ${
                        loading ? 'disabled' : ''
                      }`}
                    >
                      <span>{selectedPrefix}</span>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{
                          transform: open ? 'rotate(90deg)' : 'rotate(270deg)',
                        }}
                      >
                        <path
                          d="M12.5 13.75L8.75 10L12.5 6.25"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                  options={prefixes.map((p) => ({ id: p, label: p }))}
                  onSelect={(opt: DropdownOption) => {
                    trackFieldStart('countryCode');
                    trackEvent(AnalyticsIds.leadFormFieldInteract, {
                      parent: leadFormName,
                      primaryTypeName: 'dropDown',
                      secondaryTypeName: 'countryCode',
                      tertiaryTypeName: String(opt.id),
                    });
                    setSelectedPrefix(opt.id as string);
                  }}
                  className="w-full"
                  maxWidth={80}
                />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <input
                type={type === 'phone' ? 'tel' : type}
                className={`save-dialog__input-field ${
                  StatLinkClassProp.statInputField
                } ${hasError ? 'error' : ''} ${loading ? 'disabled' : ''}`}
                placeholder={placeholder}
                value={value}
                disabled={loading}
                onChange={(e) => {
                  let val = e.target.value;
                  if (backendError) setBackendError(null);
                  if (val.length === 1 && val === ' ') return;

                  if (type === 'text') {
                    if (!/^[a-zA-Z\s]*$/.test(val)) return;
                  } else if (type === 'phone') {
                    val = val.replace(/[^0-9]/g, '').slice(0, 9);
                  }

                  setFieldValue(id, val);
                  clearError(id);
                }}
                onFocus={() => trackFieldStart(id)}
                onBlur={() => {
                  validateSingleField(id);
                  if (value) {
                    trackEvent(AnalyticsIds.leadFormFieldInteract, {
                      parent: leadFormName,
                      primaryTypeName: 'textbox',
                      secondaryTypeName: id,
                    });
                  }
                }}
              />
            </div>
          </div>
          <div className="save-dialog__error-container">
            {hasError && <InlineError message={errors[id]} />}
          </div>
        </div>
      );
    }

    if (type === 'date' || type === 'time') {
      return (
        <div className="save-dialog__input-container" key={id}>
          <input
            type={type}
            className={`save-dialog__input-field save-dialog__input-field--${type} ${
              StatLinkClassProp.statInputField
            } ${hasError ? 'error' : ''} ${loading ? 'disabled' : ''}`}
            aria-label={label}
            value={value}
            disabled={loading}
            min={type === 'date' ? today : undefined}
            // Keep the picker Gregorian/Latin and left-to-right in Arabic too,
            // and in the same DD/MM/YYYY order the WhatsApp message will show.
            lang="en-GB"
            dir="ltr"
            onChange={(e) => {
              if (backendError) setBackendError(null);
              setFieldValue(id, e.target.value);
              clearError(id);
            }}
            onFocus={() => trackFieldStart(id)}
            onBlur={() => {
              validateSingleField(id);
              if (value) {
                trackEvent(AnalyticsIds.leadFormFieldInteract, {
                  parent: leadFormName,
                  primaryTypeName: type === 'date' ? 'datePicker' : 'timePicker',
                  secondaryTypeName: id,
                });
              }
            }}
          />
          <div className="save-dialog__error-container">
            {hasError && <InlineError message={errors[id]} />}
          </div>
        </div>
      );
    }

    if (type === 'select') {
      const options =
        field.optionsSource === 'dealers'
          ? dealers.map((d) => ({
              id: d.id,
              label: d.name,
            }))
          : models.map((m) => ({ id: m, label: m }));

      return (
        <div className="save-dialog__input-container" key={id}>
          <Dropdown
            disabled={loading}
            trigger={({ open }: { open: boolean }) => (
              <div
                className={`save-dialog__select-field ${
                  StatLinkClassProp.statdropdown
                } ${hasError ? 'error' : ''} ${loading ? 'disabled' : ''}`}
              >
                <span>{value || placeholder}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    transform: open ? 'rotate(90deg)' : 'rotate(270deg)',
                  }}
                >
                  <path
                    d="M12.5 13.75L8.75 10L12.5 6.25"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
            options={options}
            onSelect={(opt: DropdownOption) => {
              trackFieldStart(id);
              trackEvent(AnalyticsIds.leadFormFieldInteract, {
                parent: leadFormName,
                primaryTypeName: 'dropDown',
                secondaryTypeName: id,
                tertiaryTypeName: String(opt.label),
              });
              setFieldValue(id, opt.label);
              if (backendError) setBackendError(null);
            }}
            className="w-full"
            itemsClassName="w-full"
            maxWidth={600}
            position="bottom"
            matchTriggerWidth={true}
            zIndex={100000}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
          <div className="save-dialog__error-container">
            {hasError && (
              <span className="save-dialog__error-text">{errors[id]}</span>
            )}
          </div>
        </div>
      );
    }

    if (type === 'textarea') {
      return (
        <div className="save-dialog__input-container" key={id}>
          <textarea
            className={`save-dialog__input-field ${
              StatLinkClassProp.statInputField
            } ${loading ? 'disabled' : ''}`}
            placeholder={placeholder}
            value={value}
            disabled={loading}
            onChange={(e) => {
              const val = e.target.value;
              if (backendError) setBackendError(null);
              if (!(val.length === 1 && val === ' ')) {
                setFieldValue(id, val);
              }
            }}
            onFocus={() => trackFieldStart(id)}
            onBlur={() => {
              if (value) {
                trackEvent(AnalyticsIds.leadFormFieldInteract, {
                  parent: leadFormName,
                  primaryTypeName: 'textbox',
                  secondaryTypeName: id,
                });
              }
            }}
          />
        </div>
      );
    }

    return null;
  };

  const visibleFields =
    uimetadata.saveFormConfig?.fields?.filter(
      (f) => !f.visibility || f.visibility.includes(selectedOption as string)
    ) || [];

  // Group first two fields if they are firstName and lastName for the name row
  const nameFields = visibleFields.filter((f) =>
    ['firstName', 'lastName'].includes(f.id)
  );
  // The test-drive slot renders as one date + time pair on a single row, the
  // way 1.0's `.half` row does.
  const slotFields = visibleFields.filter((f) =>
    ['testDriveDate', 'testDriveTime'].includes(f.id)
  );

  return (
    <div className="save-dialog__content-wrapper save-dialog__content-wrapper--form">
      <div className="save-dialog__form-body" style={isRTL ? { paddingRight: 0, paddingLeft: '12px' } : undefined}>
        <div className="save-dialog__form-group">
          <FormError message={backendError || ''} />

          {nameFields.length > 0 && (
            <div className="save-dialog__input-row save-dialog__input-row--name-row">
              {nameFields.map(renderField)}
            </div>
          )}

          {/* Kept in metadata order, with the date/time pair collapsed into a
              single row at the position the first of the two occupies. */}
          {visibleFields
            .filter((f) => !['firstName', 'lastName'].includes(f.id))
            .map((field) => {
              if (field.id === 'testDriveTime') return null;
              if (field.id === 'testDriveDate') {
                return (
                  <div
                    className="save-dialog__input-row save-dialog__input-row--slot-row"
                    key="test-drive-slot"
                  >
                    {slotFields.map(renderField)}
                  </div>
                );
              }
              return (
                <div className="save-dialog__input-row" key={field.id}>
                  {renderField(field)}
                </div>
              );
            })}

          {errors.submit && (
            <p
              className="save-dialog__error-text"
              style={{ marginTop: '10px' }}
            >
              {errors.submit}
            </p>
          )}
        </div>

        <div className="save-dialog__privacy-section">
          <h3 className="save-dialog__privacy-title">
            {t('staticTranslations.saveDialog.privacyTitle')}
          </h3>
          <p className="save-dialog__privacy-text">
            {t('staticTranslations.saveDialog.privacyText')}
            <span
              className={`underline ${StatLinkClassProp.statDisclaim}`}
              data-dtm={DataDtm.Disclaimer}
              onClick={() => {
                updateGMDigitalDataSiteSection(DataDtm.Disclaimer);
                // Per-project and per-language: the privacy statement lives on
                // the brand's own site (cadillacarabia / gmcarabia / …) and has
                // an ar- variant. Projects that don't define it fall back to
                // the Chevrolet EN statement this used to be hardcoded to.
                window.open(
                  t(
                    'staticTranslations.saveDialog.privacyReadMoreUrl',
                    'https://www.chevroletarabia.com/ae-en/gm-privacy-statement'
                  ),
                  '_blank'
                );
              }}
            >
              {t('staticTranslations.saveDialog.privacyReadMore')}
            </span>
          </p>
          <div
            className={`save-dialog__checkbox-wrapper ${
              StatLinkClassProp.statcheckbox
            } ${loading ? 'disabled' : ''}`}
            onClick={() => {
              if (loading) return;
              trackEvent(AnalyticsIds.leadFormFieldInteract, {
                state: !isMarketingAccepted,
                parent: leadFormName,
                primaryTypeName: 'checkbox',
                secondaryTypeName: 'disclaimer',
              });
              setIsMarketingAccepted(!isMarketingAccepted);
            }}
            data-dtm={DataDtm.SendOffersCheckboxBookingForm}
            style={{
              pointerEvents: loading ? 'none' : 'auto',
              opacity: loading ? 0.6 : 1,
            }}
          >
            <div
              className={`save-dialog__checkbox ${
                isMarketingAccepted ? 'save-dialog__checkbox--checked' : ''
              }`}
            >
              {isMarketingAccepted && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="20"
                  viewBox="0 0 10 8"
                  fill="none"
                >
                  <path
                    d="M3.2 5.28L1.12 3.2L0 4.32L3.2 7.52L9.6 1.12L8.48 0L3.2 5.28Z"
                    fill="#fff"
                  />
                </svg>
              )}
            </div>
            <p
              className={`save-dialog__checkbox-label ${StatLinkClassProp.statcheckbox}`}
            >
              {t('staticTranslations.saveDialog.privacyMarketingConsent')}
            </p>
          </div>
        </div>
      </div>

      <div className="save-dialog__footer">
        <button
          className={`save-dialog__submit-button ${
            StatLinkClassProp.statsubmitbutton
          } ${isSent ? 'save-dialog__submit-button--sent' : ''}`}
          onClick={handleSendConfig}
          disabled={loading || isSent || !isFormValid}
          style={{ flex: 1 }}
          data-dtm={isBooking ? DataDtm.SubmitBookingForm : DataDtm.Submit}
        >
          {loading && <Spinner size={18} />}
          {isSent ? (
            <>
              {t('staticTranslations.saveDialog.buttonSent', 'Sent!')}
              <span className="save-dialog__sent-tick">
                <CheckIcon size={12} strokeWidth={3} />
              </span>
            </>
          ) : isBooking ? (
            t('staticTranslations.common.done', 'Submit')
          ) : (
            t('staticTranslations.saveDialog.buttonSend', 'Send Configuration')
          )}
        </button>
      </div>
    </div>
  );
};

export default FormView;
